import React, { FC, useMemo, memo } from 'react';
import { useTranslate } from '@im/react-localization';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Tag } from '@im/ui-lib/Tag';
import { FormFieldProps } from 'src/components/Form';
import Grid, {
  ColumnDef,
} from 'src/views/customerGroups/components/DataGridVirtual';
import { CellContext } from '@tanstack/react-table';
import { ProductByGlobalPlanId } from 'src/views/productGroups/hooks/useProducts';
import { NoRowsOverlay } from 'src/views/customerGroups/CustomerGroupDetails/members/AddMembersForm/util/SharedComponents';

export interface ProductListTableProps
  extends Pick<FormFieldProps<any>, 'error' | 'helperText'> {
  rows: ProductByGlobalPlanId[];
}

const ProductListTable: FC<ProductListTableProps> = ({
  error,
  helperText,
  rows,
}) => {
  const translate = useTranslate();

  const cellStatus = ({ getValue }: CellContext<ProductByGlobalPlanId, any>) =>
    getValue() ? (
      <Tag label={translate('ProductGroups.StatusAdded')} variant="success" />
    ) : (
      <Tag label={translate('ProductGroups.StatusNotAdded')} variant="error" />
    );

  const columns = useMemo<ColumnDef<ProductByGlobalPlanId>[]>(
    () => [
      {
        accessorFn: (product) => product.globalPlanId,
        enableSorting: false,
        header: translate('ProductGroups.GlobalPlanId'),
        id: 'globalPlanId',
      },
      {
        accessorFn: (product) => product.globalSKU,
        enableSorting: false,
        header: translate('ProductGroups.GlobalSKU'),
        id: 'globalSku',
        size: 50,
      },
      {
        accessorFn: (product) => !!product.isAdded,
        cell: cellStatus,
        header: translate('CustomerGroups.status'),
        id: 'status',
        size: 40,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  const noRowsOverlay = NoRowsOverlay({
    defaultText: translate('ProductGroups.AddProductsPlaceholderText'),
    defaultTitle: translate('ProductGroups.AddProductsPlaceholderTitle'),
    error,
    helperText,
  });

  const getNoRows = () => noRowsOverlay;
  return (
    <Stack flexGrow={1} overflow="hidden" spacing={1.5}>
      <Stack
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
      >
        <Typography
          sx={(theme) => ({ fontWeight: theme.typography.fontWeightSemiBold })}
          variant="body1"
        >
          {translate('ProductGroups.AddProductsCount', {
            count: rows.length,
          })}
        </Typography>
      </Stack>
      <Grid<ProductByGlobalPlanId>
        NoRowsOverlay={getNoRows}
        columns={columns}
        getRowId={(product) => product.id?.toString()}
        rows={rows}
        stickyHeader
      />
    </Stack>
  );
};

export default memo(ProductListTable);
